import { PermutiveClient, PermutiveEventProperties } from './permutiveEvent';

/**
 * Permutive-shaped `Pageview` event (Mosaic Phase 6, DAP-114763).
 *
 * Emitted on editorial page load with three property blocks:
 *   - `article`  : editorial content metadata (type, description)
 *   - `client`   : common client block (added by the event framework)
 *   - `geo_info` : geo enrichment from the Atlas location API
 *
 * `isp_info` is intentionally omitted (not required by Mosaic).
 */

export type PermutiveArticle = {
  description: string;
  type: string;
};

export type PermutiveGeoInfo = {
  city: string;
  continent: string;
  country: string;
  postal_code: string;
  province: string;
};

export type PageviewProperties = PermutiveEventProperties & {
  article: PermutiveArticle;
  client?: PermutiveClient;
  geo_info: PermutiveGeoInfo;
};

/** Loosely-typed geo source (PRISM's resolved location) mapped to `geo_info`. */
export type GeoSource = {
  city?: string | null;
  continent?: string | null;
  country?: string | null;
  postal_code?: string | null;
  province?: string | null;
};

export function buildGeoInfo(src: GeoSource): PermutiveGeoInfo {
  return {
    city: src.city || '',
    continent: src.continent || '',
    country: src.country || '',
    postal_code: src.postal_code || '',
    province: src.province || '',
  };
}

function metaContent(doc: Document, selector: string): string {
  const el = doc.querySelector(selector) as HTMLMetaElement | null;
  return (el?.content || '').trim();
}

/**
 * Best-effort read of editorial content metadata from the document for the
 * `article` block. Brands that expose richer metadata (e.g. a data layer with a
 * specific content type such as "recipe") can override this via the caller.
 */
export function getArticleFromDocument(doc: Document = document): PermutiveArticle {
  const type =
    metaContent(doc, 'meta[property="og:type"]') ||
    metaContent(doc, 'meta[name="content-type"]') ||
    metaContent(doc, 'meta[property="article:section"]');
  const description =
    metaContent(doc, 'meta[name="description"]') || metaContent(doc, 'meta[property="og:description"]');
  return { description, type };
}

/**
 * Assemble the `Pageview` properties. `article` defaults to document metadata
 * when not supplied; `client` is normally injected by the event framework and
 * only set here when explicitly overridden.
 */
export function buildPageviewProperties(args: {
  article?: PermutiveArticle;
  geo_info: PermutiveGeoInfo;
  client?: PermutiveClient;
  doc?: Document;
}): PageviewProperties {
  const properties: PageviewProperties = {
    article: args.article || getArticleFromDocument(args.doc),
    geo_info: args.geo_info,
  };
  if (args.client) {
    properties.client = args.client;
  }
  return properties;
}
